/*
 * Purpose: Mission task receipts viewer — live per-task audit alongside dossier
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { useMemo } from 'react';
import { Receipt, ScrollText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useMissionTaskReceiptStore } from '@/stores/missionTaskReceiptStore';
import { useMissionNotificationStore } from '@/stores/missionNotificationStore';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { MISSION_LIVE_DOCUMENTATION_RULE } from '@/lib/missionLiveDocumentationPolicy';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

interface MissionTaskReceiptsPanelProps {
  className?: string;
}

export function MissionTaskReceiptsPanel({ className }: MissionTaskReceiptsPanelProps) {
  const missionSessionId = useMissionKnowledgeStore((s) => s.missionSessionId);
  const receipts = useMissionTaskReceiptStore((s) => s.receipts);
  const unreadReceipts = useMissionNotificationStore((s) => s.unreadReceipts);

  useEffect(() => {
    if (receipts.length > 0) {
      useMissionNotificationStore.getState().markReceiptsSeen();
    }
  }, [receipts.length]);

  const exportJson = useMemo(
    () =>
      JSON.stringify(
        {
          missionSessionId,
          policy: MISSION_LIVE_DOCUMENTATION_RULE,
          receipts,
        },
        null,
        2
      ),
    [missionSessionId, receipts]
  );

  function downloadReceipts() {
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${missionSessionId || 'mission'}-task-receipts.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      id="mission-task-receipts"
      className={cn(
        'rounded-md border border-border/60 bg-background/80 p-3 space-y-3',
        className
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <Receipt className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground flex flex-wrap items-center gap-2">
              Mission task receipts
              {unreadReceipts > 0 && (
                <Badge variant="default" className="text-[10px] font-normal h-5">
                  {unreadReceipts} new
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px] font-normal">
                Live documented
              </Badge>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              One receipt per AI/system action — linked to dossier, map trail, and mission DB.
              Open-ended work is fine when everything lands here.
            </p>
          </div>
        </div>
        {receipts.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="shrink-0 h-8 text-xs"
            onClick={downloadReceipts}
          >
            Download JSON
          </Button>
        )}
      </div>

      {receipts.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic flex items-center gap-1.5">
          <ScrollText className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Receipts appear as the Mission runs — interpret, plan, map patches, discovery.
        </p>
      ) : (
        <ol className="max-h-48 overflow-y-auto space-y-2 pr-1 text-[11px]">
          {[...receipts].reverse().map((r) => (
            <li
              key={r.id}
              className="rounded border border-border/50 bg-muted/20 px-2.5 py-2 leading-relaxed"
            >
              <div className="flex flex-wrap gap-1.5 mb-1">
                <Badge variant="secondary" className="text-[9px] h-5">
                  MTR #{String(r.seq).padStart(3, '0')}
                </Badge>
                <Badge variant="outline" className="text-[9px] h-5">
                  {r.kind}
                </Badge>
              </div>
              <p className="text-foreground font-medium">{r.title}</p>
              <p className="text-muted-foreground mt-0.5">{r.summary}</p>
              {r.mapDelta && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Map: +{r.mapDelta.addedNodes ?? 0} nodes / −{r.mapDelta.removedNodes ?? 0} nodes
                  {r.mapDelta.rationale ? ` · ${r.mapDelta.rationale}` : ''}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
